import React from "react";
import { Button, Form } from "react-bootstrap";
import { useLingui } from "@lingui/react/macro";

import type { IPlantData } from "@/lib/backend/manufacturing2.generated.types";
import type { ModelsStagingFilterState } from "./modelsStaging.lib";

type Props = {
    plants: IPlantData[];
    filters: ModelsStagingFilterState;
    loading: boolean;
    onFiltersChange: (filters: ModelsStagingFilterState) => void;
    onSearch: () => void;
};

const ModelsStagingFilters = ({ plants, filters, loading, onFiltersChange, onSearch }: Props) => {
    const { t } = useLingui();

    const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = event.target;
        onFiltersChange({
            ...filters,
            [name]: value,
        });
    };

    const handleSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        onSearch();
    };

    return (
        <form className="models-staging-filters d-flex flex-wrap align-items-end gap-2 mb-3" onSubmit={handleSubmit}>
            <Form.Group className="mb-0">
                <Form.Label className="mb-1">{t`Plant`}</Form.Label>
                <select
                    name="plant_uuid"
                    className="form-control select_filter_control"
                    value={filters.plant_uuid}
                    onChange={handleChange}
                >
                    <option value="">{t`All plants`}</option>
                    {plants.map(plant => (
                        <option key={plant.uuid} value={plant.uuid}>
                            {plant.title}
                        </option>
                    ))}
                </select>
            </Form.Group>

            <Form.Group className="mb-0">
                <Form.Label className="mb-1">{t`Material`}</Form.Label>
                <Form.Control
                    type="text"
                    name="material_external_id"
                    placeholder={t`Material external ID`}
                    value={filters.material_external_id}
                    onChange={handleChange}
                />
            </Form.Group>

            <Form.Group className="mb-0">
                <Form.Label className="mb-1">{t`Line`}</Form.Label>
                <Form.Control
                    type="text"
                    name="line_external_id"
                    placeholder={t`Line external ID`}
                    value={filters.line_external_id}
                    onChange={handleChange}
                />
            </Form.Group>

            <Form.Group className="mb-0">
                <Form.Label className="mb-1">{t`Process`}</Form.Label>
                <Form.Control
                    type="text"
                    name="process_num"
                    placeholder={t`Process number`}
                    value={filters.process_num}
                    onChange={handleChange}
                />
            </Form.Group>

            <Form.Group className="mb-0">
                <Form.Label className="mb-1">{t`Min. diff %`}</Form.Label>
                <Form.Control
                    type="text"
                    name="min_diff_pct"
                    placeholder={t`e.g. 5`}
                    value={filters.min_diff_pct}
                    onChange={handleChange}
                />
            </Form.Group>

            <Form.Group className="mb-0 d-flex align-items-end">
                <label className="mb-0 d-flex align-items-center gap-1">
                    <input
                        type="checkbox"
                        checked={filters.pending_only}
                        onChange={event =>
                            onFiltersChange({
                                ...filters,
                                pending_only: event.target.checked,
                            })
                        }
                    />
                    {t`Pending version only`}
                </label>
            </Form.Group>

            <Button type="submit" variant="primary" disabled={loading}>
                {t`Search`}
            </Button>
        </form>
    );
};

export default ModelsStagingFilters;
